import { ErrorIcon } from "@echo-health/icons-web";
import { History } from "history";
import React, { useState, useMemo, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RouteComponentProps } from "react-router-dom";
import { ButtonGroup } from "../../components/DeprecatedButton";
import Divider from "../../components/Divider";
import { ErrorMessage } from "../../components/ErrorMessage";
import Icon from "../../components/Icon";
import DeprecatedModal from "../../components/DeprecatedModal";
import { Stack } from "../../components/Stack";
import { Button } from "../../design-system/components/Button";
import { Card } from "../../design-system/components/Card";
import { CheckboxInput } from "../../design-system/components/CheckboxInput";
import { DisplayHeading } from "../../design-system/components/DisplayHeading";
import { Heading } from "../../design-system/components/Heading";
import { Text } from "../../design-system/components/Text";
import { TextLink } from "../../design-system/components/TextLink";
import { useCreateAccountMutation } from "../../graphql/hooks/useCreateAccountMutation";
import { authenticateWithIntercom } from "../../login";
import styled from "../../styled";
import { validateFormElement } from "../../utils/forms";
import SignUpContainer from "./SignUpContainer";
import Form from "../../components/Form";
import { TextInput } from "../../design-system/components/TextInput";
import { VisibilityIcon, VisibilityOffIcon } from "@echo-health/icons-web";
import { SignUpPaths } from ".";
import { LinkButton } from "../../design-system/components/LinkButton";
import { TextButton } from "../../design-system/components/TextButton";
import { State } from "../../store/types";
import { Modal } from "../../design-system/components/Modal";
import { NHSLoginButton } from "../../components/NHSLoginButton";
import { getNHSLoginAuthInfo } from "../../utils/nhsLogin";
import { isClient } from "../../global";
import Cookies from "js-cookie";
import { usePageView } from "../../hooks/usePageView";
import { trackEvent } from "../../analytics";
import { ErrorBanner } from "../../design-system/components/ErrorBanner";
import useErrorModal, { getErrorVariant } from "../../components/ErrorModal";
import { Suggestion, validateEmail, warnEmail } from "../../utils/validators";
import { ReferralBannerSignup } from "../../components/ReferralBannerSignup";
import useReferralCode from "../../hooks/useReferralCode";

const TermsLinks = styled.div`
  /* To align with the checkbox label */
  margin: 0 0 24px 32px;
`;

const LogInLinkWrapper = styled.div`
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const WrappingEmail = styled(Text)`
  overflow-wrap: break-word;
  word-wrap: break-word;
  word-break: break-word;
`;

const Content: React.FC<{ history: History }> = props => {
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [emailHelp, setEmailHelp] = useState<Suggestion | undefined>();
  const [duplicateEmail, setDuplicateEmail] = useState("");
  const [password, setPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [hasConsentError, setHasConsentError] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isTermsChecked, setIsTermsChecked] = useState(false);
  const [isMarketingChecked, setIsMarketingChecked] = useState(false);
  const [errorModal, setErrorModal] = useState({
    isOpen: false,
    error: ""
  });
  const [isNhsModalOpen, toggleNhsModalOpen] = useState(false);
  const dispatch = useDispatch();
  const flags = useSelector((state: State) => state.features.flags);

  const nhsLoginInfo = useMemo(() => getNHSLoginAuthInfo(), []);

  const { referralCodeId, referralName, referralValue } = useReferralCode();

  useEffect(() => {
    if (referralCodeId) {
      trackEvent("referrals_account_info_pageview");
    }
  }, [referralCodeId]);

  useEffect(() => {
    if (isClient()) {
      Cookies.set("nhs_login_auth_state", nhsLoginInfo.state, {
        domain: ".echo.co.uk",
        secure: true
      });
      Cookies.set("nhs_login_nonce", nhsLoginInfo.nonce, {
        domain: ".echo.co.uk",
        secure: true
      });
    }
  }, [nhsLoginInfo]);

  const { handleError } = useErrorModal();

  const retryCreateAccount = () => {
    createAccount({
      variables: {
        email: email.trim(),
        password: password,
        consentMarketingEmail: isMarketingChecked,
        consentMarketingPost: isMarketingChecked,
        referralCodeId: referralCodeId
      }
    });
  };

  const [createAccount, { loading, error }] = useCreateAccountMutation({
    onCompleted: res => {
      const { account, errors } = res.createAccount;

      if (errors.length > 0 || account === null) {
        const duplicateEmailError = errors.find(
          error => error.field === "email" && error.message === "already exists"
        );

        if (duplicateEmailError) {
          trackEvent("signup_error_account_duplicate", {
            type: "create_account "
          });
          setDuplicateEmail(account ? account.email : email);
        } else {
          handleError({
            onRetry: retryCreateAccount
          });
        }

        return;
      }

      authenticateWithIntercom({
        accountId: account.id,
        firstName: account.firstName,
        lastName: account.lastName,
        email: account.email,
        intercomId: account.intercomId,
        dispatch,
        digitalMarketing: isMarketingChecked,
        intercomUserHash: account.intercomUserHash
      });

      props.history.push(SignUpPaths.SIGNUP_EMAIL_VERIFICATION);
    },
    onError: err => {
      handleError({
        variant: getErrorVariant(err),
        onRetry: retryCreateAccount
      });
    }
  });

  const isNhsLoginEnabled =
    flags &&
    flags.length > 0 &&
    flags.find(flag => flag.name === "nhs_login_login" && flag.isEnabled);

  return (
    <>
      <Card>
        {/* <CaptifyPixel pixelId={"1176866"} /> */}

        {referralValue && referralName && (
          <ReferralBannerSignup
            referralName={referralName}
            referralValue={referralValue}
          />
        )}

        <DisplayHeading as="h1" margin="0 0 m 0">
          Tell us about yourself
        </DisplayHeading>
        <Text margin={isNhsLoginEnabled ? "0 0 s" : "0 0 l"}>
          To get started, enter your email address and create a password.
        </Text>
        {isNhsLoginEnabled && (
          <Text margin="0 0 l">
            Or you can{" "}
            <TextButton
              label="use your NHS login"
              onClick={() => {
                toggleNhsModalOpen(true);
                trackEvent("signup_nhs_login_info");
              }}
            />
            .
          </Text>
        )}

        <div aria-live="polite">
          {duplicateEmail && (
            <ErrorBanner
              title="There was a problem creating your account"
              margin="0 0 l"
            >
              <Text>
                We’ve sent an email to{" "}
                <WrappingEmail isBold isInline>
                  {duplicateEmail}
                </WrappingEmail>{" "}
                with more information.
              </Text>
            </ErrorBanner>
          )}
        </div>

        <Form
          onSubmit={() => {
            setHasConsentError(false);
            setDuplicateEmail("");
            let hasErrors = false;
            const emailErr = validateEmail(email);
            const passErr = validateFormElement("password", password);

            if (emailErr || passErr) {
              hasErrors = true;
              setEmailError(emailErr);
              setPasswordError(passErr);
            }

            if (!isTermsChecked) {
              hasErrors = true;
              setHasConsentError(true);
            }

            if (hasErrors) {
              return;
            }

            createAccount({
              variables: {
                email: email.trim(),
                password: password,
                consentMarketingEmail: isMarketingChecked,
                consentMarketingPost: isMarketingChecked,
                referralCodeId: referralCodeId
              }
            });
          }}
        >
          <TextInput
            onBlur={() => {
              setEmailError(validateEmail(email));
              setEmailHelp(warnEmail(email));
            }}
            onChange={e => {
              setEmail(e.currentTarget.value);
              setEmailError("");
              setEmailHelp(undefined);
            }}
            margin="0 0 l 0"
            label="Email"
            id="EmailId"
            name="email"
            value={email}
            errorMessage={emailError}
            helpMessage={
              emailHelp ? (
                <>
                  Did you mean{" "}
                  <TextButton
                    size="s"
                    label={emailHelp.full}
                    aria-label={`Change email to ${emailHelp.full}`}
                    onClick={() => {
                      trackEvent("signup_email_typo_fix");
                      setEmail(emailHelp.full);
                      setEmailHelp(undefined);
                    }}
                  />
                  ?
                </>
              ) : (
                undefined
              )
            }
            autoComplete="email"
          />

          <TextInput
            onBlur={() => {
              setPasswordError(validateFormElement("password", password));
            }}
            onChange={e => {
              setPassword(e.currentTarget.value);
              setPasswordError("");
            }}
            margin="0 0 l 0"
            type={showPassword ? "text" : "password"}
            label="Password"
            helpMessage="Your password must be at least 6 characters long"
            id="passwordId"
            name="password"
            value={password}
            errorMessage={passwordError}
            autoComplete="new-password"
            iconTrailing={{
              icon: showPassword ? VisibilityIcon : VisibilityOffIcon,
              type: "button",
              onClick: () => {
                setShowPassword(!showPassword);
              },
              "aria-label": showPassword ? "Hide password" : "Show password"
            }}
          />

          <Divider margin="0 0 l" />

          <CheckboxInput
            label="I agree to Echo's terms and conditions and privacy policy."
            checked={isTermsChecked}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setHasConsentError(false);
              setIsTermsChecked(!isTermsChecked);
            }}
          />

          <TermsLinks>
            <Stack distribution="leading">
              <Text size="s" margin={hasConsentError ? "0 0 s 0" : "0"}>
                Read the{" "}
                <TextLink
                  size="s"
                  type="external"
                  linkTo="https://www.echo.co.uk/p/terms-and-conditions?utm_source=echo&utm_medium=webview&utm_campaign=legal_terms"
                  label="terms and conditions"
                />{" "}
                and{" "}
                <TextLink
                  size="s"
                  type="external"
                  label="privacy policy"
                  linkTo="https://www.echo.co.uk/p/privacy-policy?utm_source=echo&utm_medium=webview&utm_campaign=privacy_policy"
                />
                .
              </Text>
            </Stack>
            {hasConsentError && (
              <Text color="red70" size="s">
                Please agree to the terms and conditions and privacy policy.
              </Text>
            )}
          </TermsLinks>

          <CheckboxInput
            label="Keep me updated on special offers from LloydsPharmacy and our partners."
            checked={isMarketingChecked}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setIsMarketingChecked(!isMarketingChecked);
            }}
            margin="0 0 l"
          />

          <Divider margin="0 0 l" />

          <ButtonGroup>
            <Button
              shouldSubmit={true}
              label="Continue"
              isLoading={loading}
              width="fill"
            />
            <LogInLinkWrapper>
              <Text color="gray70">
                Already have an account?{" "}
                <TextLink label="Log in" linkTo="/login" />
              </Text>
            </LogInLinkWrapper>
          </ButtonGroup>
        </Form>
      </Card>
      <DeprecatedModal
        isOpen={errorModal.isOpen}
        onRequestClose={() => setErrorModal({ isOpen: false, error: "" })}
      >
        <Stack vertical={true} distribution="center" alignment="center">
          <Icon type={ErrorIcon} size="xl" margin="0 0 m 0" />
          <Heading margin="0 0 m 0">We could not create your account</Heading>
          <ErrorMessage
            showContactSupportButton={false}
            error={errorModal.error || error}
            isCentered={true}
            margin="0 0 l"
          />
          <LinkButton linkTo="/login" label="Log in to Echo" />
        </Stack>
      </DeprecatedModal>
      {isNhsLoginEnabled && (
        <Modal
          title="Sign up with NHS login"
          isOpen={isNhsModalOpen}
          onClose={() => toggleNhsModalOpen(false)}
        >
          <Text margin="0 0 m">
            If you have an NHS login, you can use it to sign up to Echo. This
            will let us automatically add all your medicine and account details.
          </Text>
          <NHSLoginButton
            type="signup_continue"
            href={nhsLoginInfo ? nhsLoginInfo.authUrl : ""}
          />
          <TextLink
            margin="m 0 0 0"
            label="Learn more about NHS login"
            linkTo="https://www.nhs.uk/using-the-nhs/nhs-services/nhs-login/"
            onClick={() => trackEvent("signup_nhs_login_learn_more")}
          />
        </Modal>
      )}
    </>
  );
};

const CreateAccount: React.FC<RouteComponentProps> = props => {
  const isAuthenticated = useSelector((state: State) => {
    return state.auth.authenticated;
  });

  usePageView(track => {
    if (!isAuthenticated) {
      track("signup_account_info_pageview");
    }
  });

  return (
    <SignUpContainer>
      {() => {
        return <Content history={props.history} />;
      }}
    </SignUpContainer>
  );
};

export default CreateAccount;
